import { SubstepType } from "~/features/configurator/Linear/dtos";
import {
  makeLabelGetter,
  makeNumberGetter,
  prepareSubStepsInfo,
  type SummaryData,
  type SummaryDataProvider,
} from "~/features/configurator/Shaft/SummaryDataProvider";
import { removeMultipleTags } from "~/common/utils/domUtils";
import { CsticNames } from "~/features/configurator/Carriage/components/ConfigureCarriageSubstep/constants";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import { getMainKmatCstics } from "~/features/configurator/common/v2/helper";
import type { LinearCstic } from "~/features/configurator/Linear/models";

export function getStep1SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps = [SubstepType.Coating, SubstepType.Preload, SubstepType.Accuracy, SubstepType.Lubrication];
  return prepareSubStepsInfo(summaryProvider, substeps);
}

export function getStep2SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const t = summaryProvider.t;

  const substeps = [SubstepType.CarriageType, SubstepType.LocatingFace];
  const summaryData = prepareSubStepsInfo(summaryProvider, substeps) ?? [];

  const configureCarriageSubstep = summaryProvider.substeps[SubstepType.ConfigureCarriage];
  if (configureCarriageSubstep) {
    const inputCsticNames = [
      CsticNames.KITLeft,
      CsticNames.KITCenter,
      CsticNames.KITRight,
      CsticNames.LubricationConnectorOrientation,
      CsticNames.LubricationConnectorPosition,
      CsticNames.LubricationConnector,
    ];

    const inputCsticNamesFormatting: Record<string, (cstic: LinearCstic) => string> = {
      [CsticNames.KITLeft]: makeNumberGetter(),
      [CsticNames.KITCenter]: makeNumberGetter(),
      [CsticNames.KITRight]: makeNumberGetter(),
      [CsticNames.LubricationConnectorOrientation]: makeLabelGetter(t),
      [CsticNames.LubricationConnectorPosition]: makeLabelGetter(t),
      [CsticNames.LubricationConnector]: makeLabelGetter(t),
    };

    const inputCstics = getMainKmatCstics(configureCarriageSubstep).input;
    const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;

    inputCsticNames.forEach((inputCsticName) => {
      const def = inputCstics[inputCsticName];
      const cstic = cstics[def.name];
      const getValue = inputCsticNamesFormatting[inputCsticName];

      if (!cstic || !def.visible) {
        return;
      }

      summaryData.push({
        name: t(cstic.label),
        isStandard: false,
        value: getValue(cstic),
      });
    });
  }

  summaryData.forEach((data) => {
    data.value = removeMultipleTags(data.value, "p", "span")!;
    data.name = removeMultipleTags(data.name, "p", "span")!;
  });

  return summaryData;
}
